/**
 * @Author: Anandp
 * @Date:   2024-04-17 18:40:50
 * @Last Modified by:   Anandp
 * @Last Modified time: 2025-03-07 10:09:28
 */
import React, { useEffect, useState } from "react";
import { DataGrid } from '@mui/x-data-grid';
import { Box, Paper, IconButton, TextField } from '@mui/material';
import { Edit, Save, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import customerApiService from '../../services/customerApi';

function OrgStructure() {
  const accessToken = sessionStorage.getItem("accessToken");
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [editingRows, setEditingRows] = useState({});
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchOrganizations();
    // eslint-disable-next-line
  }, [paginationModel, searchText]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await customerApiService.getpaginatedOrganization(
        accessToken,
        paginationModel.page,
        paginationModel.pageSize,
        searchText
      );
      if (response && response.slicedOrganization) {
        const formattedOrgs = response.slicedOrganization.map((org, index) => ({
          id: org.category_id || index,
          slNo: (paginationModel.page * paginationModel.pageSize) + index + 1,
          name: org.name || '',
          typeName: org.type_name || '',
          uniqueCode: org.object_id || '',
          isEditing: false
        }));
        setOrganizations(formattedOrgs);
        setTotalCount(response.totalOrganization || 0);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    setEditingRows(prev => ({ ...prev, [id]: true }));
  };

  const handleSave = async (id) => {
    try {
      const row = organizations.find(org => org.id === id);
      
      if (row.uniqueCode !== undefined) {
        const response = await customerApiService.updateOrgObjectId({
          orgId: row.id,
          objectId: row.uniqueCode
        });
        
        if (response.success) {
          toast.success('Object ID updated successfully!');
          setEditingRows(prev => ({ ...prev, [id]: false }));
        } else {
          toast.error(response.message || 'Failed to update object ID');
          if (response.existingEntity) {
            // toast.error(`ID already exists for ${response.existingEntity.type}: ${response.existingEntity.name}`);
          }
        }
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error updating object ID');
    }
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'Sl No',
      width: 100,
      editable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      editable: false,
    },
    {
      field: 'typeName',
      headerName: 'Type Name',
      width: 250,
      editable: false,
    },
    {
      field: 'uniqueCode',
      headerName: 'Object ID',
      width: 300,
      editable: false,
      renderCell: (params) => {
        const isEditing = !!editingRows[params.id];
        
        if (isEditing) {
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              width: '100%',
              px: 1
            }}>
              <input
                type="text"
                value={params.value || ''}
                autoFocus
                onChange={(e) => {
                  const newValue = e.target.value;
                  setOrganizations(prev => 
                    prev.map(org => 
                      org.id === params.id ? { ...org, uniqueCode: newValue } : org
                    )
                  );
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  width: '100%',
                  fontSize: '14px'
                }}
              />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave(params.id);
                }}
                color="primary"
                size="small"
                sx={{ ml: 1 }}
              >
                <Save fontSize="small" />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            '&:hover .edit-button': {
              opacity: 1
            }
          }}>
            <span>{params.value}</span>
            <IconButton
              className="edit-button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(params.id);
              }}
              color="primary"
              size="small"
              sx={{ 
                opacity: 0,
                transition: 'opacity 0.2s',
                ml: 1
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ height: 400,margin:5, width: '90%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, width: '90%' }}>
        <TextField
          placeholder=""
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPaginationModel(prev => ({ ...prev, page: 0 }));
          }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ width: '300px' }}
          size="small"
        />
      </Box>
      
        <DataGrid
          rows={organizations}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          processRowUpdate={(newRow) => {
            setOrganizations(prev => 
              prev.map(org => org.id === newRow.id ? newRow : org)
            );
            return newRow;
          }}
          sx={{
            '& .MuiDataGrid-cell--editable': {
              backgroundColor: '#bbdefb',
              '&:hover': {
                backgroundColor: '#bbdefb',
              },
            },
            '& .MuiDataGrid-cell:hover .edit-button': {
              opacity: 1,
            },
          }}
        />
      </Paper>
    </Box>
  );
}

export default OrgStructure;
